import React, { useState, useRef, useEffect, JSX } from 'react';
import {
    SendHorizonal,
    FileBarChart2,
    Bot,
    Maximize2,
    Minimize2,
    ListChecks,
    Filter,
    Check,
    Copy,
    Clock,
} from 'lucide-react';
import { ingestionService } from '../../components/api/ingestionService';
import ReactMarkdown from 'react-markdown';
import { apiService } from '../../components/api/apiService';
import BookingForm from './dynamicforms/BookingForm';
import WorkflowApprovalForm from './dynamicforms/WorkflowApprovalForm';
import ExpenseReimbursementForm from './dynamicforms/ExpenseReimbursementForm';
import GPTResponseRenderer from './GPTResponseRenderer';
import DynamicDataTable from './dynamicforms/DynamicDataTable';
import ChartDataView from './dynamicforms/ChartDataView';

type ChatMessage = {
    sender: 'user' | 'agent';
    content: string | JSX.Element;
    type?: 'text' | 'report';
    timestamp?: string;
    usedContexts?: {
        contextid: string;
        collection_name: string;
        datasourceid: string;
        chunk_count: number;
    }[];
};

const chartImageMap: Record<string, string> = {
    comparison: '/assets/charts/comparison.png',
    deliverables: '/assets/charts/deliverables.jpg',
    distribution: '/assets/charts/distribution.png',
    ecemxproject: '/assets/charts/eCemxProject.png',
    forecast: '/assets/charts/forecast.png',
    gantt: '/assets/charts/gantt.png',
    milestones: '/assets/charts/milestones.jpg',
    teamprogress: '/assets/charts/teamprogress.png'
};


const TypingIndicator = () => {
    return (
        <div className="flex items-center space-x-1 px-4 py-2 bg-gray-100 rounded-lg w-fit animate-pulse">
            <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
            <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
            <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" />
        </div>
    );
};

const initialMessages: ChatMessage[] = [
    {
        sender: 'agent',
        content: 'Hi there! I am Selfic Agent. How can I assist you today?',
        type: 'text',
    }
];
type ContextItem = {
    id: string;
    title: string;
    collection_namespace: string;
    data_source_id: string;
};

type ChatTabState = {
    messages: ChatMessage[];
    input: string;
    selectedContexts: ContextItem[];
};

type Props = {
    tabId: string;
    messages: ChatMessage[];
    input: string;
    selectedContexts: ContextItem[];
    sessionId: string;
    language: string;
    onStateChange: (newState: Partial<ChatTabState>) => void;
};

type AvailableContext = {
    data_source_id: string;
    category: string;
    items: ContextItem[];
};

const ChatAgentPanel: React.FC<Props> = (props) => {
    const [input, setInput] = useState(props.input);
    const [messages, setMessages] = useState<ChatMessage[]>(props.messages);
    const [language, setLanguage] = useState(props.language);

    // const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
    // const [input, setInput] = useState('');
    const chatRef = useRef<HTMLDivElement>(null);
    const [isTyping, setIsTyping] = useState(false);

    const handleSend = async () => {
        if (!input.trim()) return;

        const userMessage: ChatMessage = {
            sender: 'user',
            content: input,
            type: 'text',
        };

        setInput('');
        props.onStateChange({ input: '' });

        setMessages((prev) => {
            const updated = [...prev, userMessage];
            props.onStateChange({ messages: updated });
            return updated;
        });

        setIsTyping(true);

        const lowerMsg = input.toLowerCase();
        const chartMatch = false;
        // Object.keys(chartImageMap).find((key) =>
        //     lowerMsg.includes(key)
        // );

        // Handle static forms
        if (lowerMsg.includes("booking workflow")) {
            const bookingReply: ChatMessage = {
                sender: 'agent',
                content: <BookingForm />
            };
            setMessages((prev) => {
                const updated = [...prev, bookingReply];
                props.onStateChange({ messages: updated });
                return updated;
            });
            setIsTyping(false);
            return;
        }

        if (lowerMsg.includes("workflow approval")) {
            const reply: ChatMessage = {
                sender: 'agent',
                content: <WorkflowApprovalForm />
            };
            setMessages((prev) => {
                const updated = [...prev, reply];
                props.onStateChange({ messages: updated });
                return updated;
            });
            setIsTyping(false);
            return;
        }

        if (lowerMsg.includes("expense reimbursement")) {
            const reply: ChatMessage = {
                sender: 'agent',
                content: <ExpenseReimbursementForm />
            };
            setMessages((prev) => {
                const updated = [...prev, reply];
                props.onStateChange({ messages: updated });
                return updated;
            });
            setIsTyping(false);
            return;
        }

        // Handle predefined chart matches
        if (chartMatch) {
            const chartReply: ChatMessage = {
                sender: 'agent',
                content: (
                    <img
                        src={chartImageMap[chartMatch]}
                        alt={chartMatch}
                        className="rounded border border-neutral-700 max-w-full"
                    />
                ),
                type: 'report',
            };
            setMessages((prev) => {
                const updated = [...prev, chartReply];
                props.onStateChange({ messages: updated });
                return updated;
            });
            setIsTyping(false);
            return;
        }

        const defaultContextUsed = selectedContexts.length === 0;

        // Group contexts by data_source_id
        const grouped =
            selectedContexts.length > 0
                ? selectedContexts.reduce((acc, ctx) => {
                    if (
                        ctx.collection_namespace.toLowerCase().includes('projectmanagement')
                    )
                        return acc; // Skip projectmanagement
                    if (!acc[ctx.data_source_id]) acc[ctx.data_source_id] = [];
                    acc[ctx.data_source_id].push({
                        contextid: ctx.id,
                        contextcollectionname: ctx.collection_namespace,
                    });
                    return acc;
                }, {} as Record<string, { contextid: string; contextcollectionname: string }[]>)
                : availableContexts.reduce((acc, group) => {
                    const contexts = group.items
                        .filter(
                            (ctx) =>
                                !ctx.collection_namespace
                                    .toLowerCase()
                                    .includes('projectmanagement')
                        )
                        .map((ctx) => ({
                            contextid: ctx.id,
                            contextcollectionname: ctx.collection_namespace,
                        }));

                    if (contexts.length > 0) {
                        acc[group.data_source_id] = contexts;
                    }

                    return acc;
                }, {} as Record<string, { contextid: string; contextcollectionname: string }[]>);

        const datasources = Object.entries(grouped).map(([datasourceid, contexts]) => ({
            datasourceid,
            typeofcontext: 'pdf', // can be dynamic
            contexts,
        }));

        const includesProjectManagement = selectedContexts.some((ctx) =>
            ctx.collection_namespace.toLowerCase().includes("project")
        );

        try {
            let res;
            if (includesProjectManagement) {
                res = await ingestionService.generateQuery({
                    tenant_id: "6a8ab55d-8167-4fc4-a61b-caea800a1508",
                    query: input
                });

                const { result, responseType, llmresponse, used_contexts } = res.sql || {};

                const reply: ChatMessage = {
                    sender: 'agent',
                    content: responseType === 'data'
                        ? <ChartDataView data={result} />
                        : llmresponse || 'No response received.',
                    type: responseType === 'data' ? 'report' : 'text',
                    usedContexts: used_contexts || [],
                };

                setMessages((prev) => {
                    const updated = [...prev, reply];
                    props.onStateChange({ messages: updated });
                    return updated;
                });
            } else {
                const payload = {
                    sessionid: props.sessionId,
                    query: input,
                    datasources,
                    contextfitler: !defaultContextUsed
                };
                res = await ingestionService.chatQuery(payload);

                const reply: ChatMessage = {
                    sender: 'agent',
                    content:
                        typeof res === 'string'
                            ? res
                            : res?.llmresponse || res?.answer || res?.message || 'No response received.',
                    type: 'text',
                    usedContexts: res?.used_contexts || []
                };

                setMessages((prev) => {
                    const updated = [...prev, reply];
                    props.onStateChange({ messages: updated });
                    return updated;
                });
            }
        } catch (err) {
            const failMsg: ChatMessage = {
                sender: 'agent',
                content: language === 'ar'
                    ? '❌ فشل في جلب الرد.'
                    : '❌ Failed to fetch reply.',
                type: 'text'
            };
            setMessages((prev) => {
                const updated = [...prev, failMsg];
                props.onStateChange({ messages: updated });
                return updated;
            });
        } finally {
            setIsTyping(false);
        }
    };

    const handleSend_old = async () => {
        if (!input.trim()) return;

        const userMessage: ChatMessage = {
            sender: 'user',
            content: input,
            type: 'text'
        };

        setInput('');
        props.onStateChange({ input: '' });

        setMessages((prev) => {
            const updated = [...prev, userMessage];
            props.onStateChange({ messages: updated });
            return updated;
        });

        setIsTyping(true);

        const lowerMsg = input.toLowerCase();
        const chartMatch = Object.keys(chartImageMap).find((key) =>
            lowerMsg.includes(key)
        );

        if (lowerMsg.includes("booking workflow")) {
            const bookingReply: ChatMessage = {
                sender: 'agent',
                content: <BookingForm />
            };

            setMessages((prev) => {
                const updated = [...prev, bookingReply];
                props.onStateChange({ messages: updated });
                return updated;
            });

            setIsTyping(false);
            return;
        }
        if (lowerMsg.includes("workflow approval")) {
            const bookingReply: ChatMessage = {
                sender: 'agent',
                content: <WorkflowApprovalForm />
            };

            setMessages((prev) => {
                const updated = [...prev, bookingReply];
                props.onStateChange({ messages: updated });
                return updated;
            });

            setIsTyping(false);
            return;
        }
        if (lowerMsg.includes("expense reimbursement")) {
            const bookingReply: ChatMessage = {
                sender: 'agent',
                content: <ExpenseReimbursementForm />
            };

            setMessages((prev) => {
                const updated = [...prev, bookingReply];
                props.onStateChange({ messages: updated });
                return updated;
            });

            setIsTyping(false);
            return;
        }
        if (chartMatch) {
            const chartReply: ChatMessage = {
                sender: 'agent',
                content: (
                    <img
                        src={chartImageMap[chartMatch]}
                        alt={chartMatch}
                        className="rounded border border-neutral-700 max-w-full"
                    />
                ),
                type: 'report'
            };

            setMessages((prev) => {
                const updated = [...prev, chartReply];
                props.onStateChange({ messages: updated });
                return updated;
            });

            setIsTyping(false);
            return;
        }
        const defaultContextUsed = selectedContexts.length === 0;

        // Group by datasource ID 
        const grouped =
            selectedContexts.length > 0
                ? selectedContexts.reduce((acc, ctx) => {
                    if (!acc[ctx.data_source_id]) acc[ctx.data_source_id] = [];
                    acc[ctx.data_source_id].push({
                        contextid: ctx.id,
                        contextcollectionname: ctx.collection_namespace
                    });
                    return acc;
                }, {} as Record<string, { contextid: string; contextcollectionname: string }[]>)
                : availableContexts.reduce((acc, group) => {
                    const contexts = group.items.map((ctx) => ({
                        contextid: ctx.id,
                        contextcollectionname: ctx.collection_namespace
                    }));
                    acc[group.data_source_id] = contexts;
                    return acc;
                }, {} as Record<string, { contextid: string; contextcollectionname: string }[]>);

        const datasources = Object.entries(grouped).map(([datasourceid, contexts]) => ({
            datasourceid,
            typeofcontext: 'pdf', // can make dynamic later
            contexts
        }));


        const payload = {
            sessionid: props.sessionId,
            query: input,
            datasources,
            contextfitler: !defaultContextUsed
        };
        try {
            const res = await ingestionService.chatQuery(payload);
            const agentReply: ChatMessage = {
                sender: 'agent',
                content:
                    typeof res === 'string'
                        ? res
                        : res?.llmresponse || res?.answer || res?.message || 'No response received.',
                type: 'text',
                usedContexts: res?.used_contexts || []
            };

            setMessages((prev) => {
                const updated = [...prev, agentReply];
                props.onStateChange({ messages: updated });
                return updated;
            });
        } catch (err) {
            const failMsg: ChatMessage = {
                sender: 'agent',
                content: '❌ Failed to fetch reply.',
                type: 'text'
            };

            setMessages((prev) => {
                const updated = [...prev, failMsg];
                props.onStateChange({ messages: updated });
                return updated;
            });
        } finally {
            setIsTyping(false);
        }
    };


    //============Existing contexts
    const [selectedContexts, setSelectedContexts] = useState<ContextItem[]>(props.selectedContexts);
    const [chatStates, setChatStates] = useState<Record<string, ChatTabState>>({});

    const [availableContexts, setAvailableContexts] = useState<
        AvailableContext[]
    >([]);
    useEffect(() => {
        const loadContexts = async () => {
            try {
                const response = await ingestionService.getAvailableContexts();
                console.log('Available contexts:', response);
                const structured = response.reduce((acc: AvailableContext[], ds: any) => {
                    if (!ds || !ds.contexts || ds.contexts.length === 0) return acc;

                    acc.push({
                        category: ds.name || 'Unknown',
                        data_source_id: ds.data_source_id,
                        items: ds.contexts.map((ctx: any) => ({
                            id: ctx.context_id || crypto.randomUUID(), // fallback to prevent key issues
                            title: ctx.title || 'Untitled',
                            collection_namespace: ctx.collection_namespace,
                            data_source_id: ds.data_source_id,
                        }))
                    });

                    return acc;
                }, []);
                setAvailableContexts(structured);
                
            } catch (err) {
                console.error('Failed to load contexts:', err);
            }
        };

        loadContexts();
    }, []);

    // const [selectedContexts, setSelectedContexts] = useState<string[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [hovered, setHovered] = useState<string | null>(null);

    const toggleContext = (context: {
        id: string;
        title: string;
        collection_namespace: string;
        data_source_id: string;
    }) => {
        setSelectedContexts((prev) => {
            const exists = prev.find((c) => c.id === context.id);
            const updated = exists
                ? prev.filter((c) => c.id !== context.id)
                : [...prev, context];
            props.onStateChange({ selectedContexts: updated });
            return updated;
        });
    };



    useEffect(() => {
        const container = chatRef.current;
        if (container) {
            container.scrollTo({
                top: container.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, [messages]);

    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };
        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown]);


    // useEffect(() => {
    //     chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
    // }, [messages]);

    const clearSession = async () => {
        try {
            await apiService.post(`/sessions/deleteSession/${props.sessionId}`, {}, 'selfic_core');
            console.log(`Session ${props.sessionId} deleted successfully.`);
        } catch (error) {
            console.error('Failed to delete session:', error);
        }
    };
    const [searchTerm, setSearchTerm] = useState('');
    const [copiedIndex, setCopiedIndex] = useState<number | null>(null);


    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto'; // Reset height
            textarea.style.height = Math.min(textarea.scrollHeight, 168) + 'px'; // Cap to 7 lines
        }
    }, [input]);

    const validate_context = (selectedContexts: any): string => {
        const matchFound = selectedContexts.some((ctx: any) => {
            const nam = ctx.collection_name;
            const cleanedNam = ctx.collection_name.replace(/_[0-9a-fA-F-]{36}$/, '');
            const text = JSON.stringify(cleanedNam).toLowerCase();
            return text.includes("compliance_documents_") || text.includes("selfic_Technical_document_");
        });

        return matchFound
            ? "Llama-3.3-70B-Instruct"
            : "GPT-4o via Azure";
    };

    return (
        <div className={`flex flex-col h-full w-full overflow-hidden bg-white ${language === 'ar' ? 'rtl' : 'ltr'}`}>
            <div className="flex flex-col flex-1 overflow-hidden min-h-0">

                {/* Message Body */}
                <div className="flex-1 overflow-y-auto px-4 pt-4 pb-2 space-y-4" ref={chatRef}>
                    {messages.map((msg, idx) => {
                        const isUser = msg.sender === 'user';
                        const time = new Date(msg.timestamp ?? Date.now()).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true,
                        });

                        return (
                            <div key={idx} className="flex w-full justify-center">
                                <div className="w-full px-4 sm:px-8 md:px-16 lg:px-32 xl:px-48">
                                    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`flex flex-col ${isUser ? 'max-w-[60%]' : 'max-w-[750px]'}`}>
                                            {typeof msg.content === 'string' ? (
                                                isUser ? (
                                                    <div className="px-5 py-3 text-sm leading-relaxed rounded-2xl bg-indigo-100 text-gray-900 border border-indigo-200 shadow-sm self-end">
                                                        {msg.content}
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="relative px-6 py-5 text-sm leading-relaxed rounded-2xl border backdrop-blur-sm bg-white/80 text-gray-800 border-gray-200 shadow-lg ring-1 ring-inset ring-white/40">
                                                            <GPTResponseRenderer content={msg.content} />

                                                            {/* Copy Button */}
                                                            <div className={`absolute top-2 ${language === 'ar' ? 'left-2' : 'right-2'}`}>
                                                                <button
                                                                    onClick={() => {
                                                                        navigator.clipboard.writeText(typeof msg.content === 'string' ? msg.content : '');
                                                                        setCopiedIndex(idx);
                                                                        setTimeout(() => setCopiedIndex(null), 1500);
                                                                    }}
                                                                    title={language === 'ar' ? 'نسخ' : 'Copy response'}
                                                                    className="p-1 rounded hover:bg-gray-200 transition group"
                                                                >
                                                                    {copiedIndex === idx ? (
                                                                        <Check className="w-4 h-4 text-green-500" />
                                                                    ) : (
                                                                        <Copy className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </div>

                                                        {/* Context Info */}
                                                        <div className="mt-2 text-[11px] text-gray-500 flex items-center gap-2 flex-wrap">
                                                            {Array.isArray(msg.usedContexts) && msg.usedContexts.length > 0 && (
                                                                <>
                                                                    <span className="font-medium text-gray-600">{language === 'ar' ? '📚 السياقات:' : '📚 Contexts:'}</span>
                                                                    {msg.usedContexts.map((ctx, i) => {
                                                                        const cleanedName = ctx.collection_name.replace(/_[0-9a-fA-F-]{36}$/, '');
                                                                        return (
                                                                            <span
                                                                                key={i}
                                                                                className="px-1.5 py-[1px] bg-gray-200 text-gray-700 border border-gray-300 rounded-md text-[10px] font-medium shadow-sm"
                                                                            >
                                                                                #{cleanedName}
                                                                            </span>
                                                                        );
                                                                    })}
                                                                </>
                                                            )}

                                                            <div className="flex items-center gap-1">
                                                                <Bot className="w-3.5 h-3.5 text-purple-500" />
                                                                <span>Llama-3.3-70B-Instruct</span>
                                                            </div>
                                                        </div>
                                                    </>
                                                )
                                            ) : msg.content}

                                            {/* TIMESTAMP */}
                                            <span className={`text-[10px] text-gray-400 mt-1 ${isUser ? (language === 'ar' ? 'text-left pl-1' : 'text-right pr-1') : (language === 'ar' ? 'text-right pr-1' : 'text-left pl-1')}`}>
                                                {isUser ? (language === 'ar' ? 'أنت' : 'You') : 'Selfic Agent'} • {time}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    {isTyping && (
                        <div className="flex w-full justify-start px-8" style={{ marginLeft: '160px', width: '50%' }}>
                            <div className="flex items-center gap-1 px-4 py-2 bg-gray-100 rounded-lg shadow-sm">
                                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:.1s]" />
                                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:.2s]" />
                                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:.3s]" />
                            </div>
                        </div>
                    )}
                </div>

                {/* Input Bar */}
                <div className="sticky bottom-0 z-10 bg-neutral-50 px-4 py-3 border-t border-gray-200">
                    <div className="mx-auto max-w-5xl w-full">
                        <div className="bg-white rounded-3xl border border-gray-200 shadow-sm px-4 py-3 space-y-2">

                            {/* Row 1: Input Field */}
                            <div className="w-full">
                                <textarea
                                    ref={textareaRef}
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSend();
                                        }
                                    }}
                                    placeholder={language === 'ar' ? 'كيف يمكن لـ SelficAI مساعدتك اليوم؟' : 'How can SelficAI assist you today?'}
                                    rows={1}
                                    className="w-full resize-none text-sm placeholder-gray-400 text-gray-800 bg-transparent px-1.5 py-1 focus:outline-none focus:ring-0 overflow-y-auto"
                                    style={{ maxHeight: '168px', lineHeight: '24px' }}
                                />

                                {showDropdown && (
                                    <div
                                        ref={dropdownRef}
                                        className="absolute bottom-[90px] left-16 sm:left-20 md:left-32 lg:left-48 xl:left-64 z-50 w-[320px] rounded-xl  shadow-2xl bg-white backdrop-blur-md"
                                    >
                                        <div className="p-3">
                                            <input
                                                type="text"
                                                style={{ color: 'gray' }}
                                                placeholder="Search files, docs..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-indigo-400"
                                            />
                                            <div className="max-h-64 overflow-y-auto space-y-2 mt-2">
                                                {availableContexts.map((group) => {
                                                    const filteredItems = group.items.filter((item) =>
                                                        item.title.toLowerCase().includes(searchTerm.toLowerCase())
                                                    );

                                                    if (filteredItems.length === 0) return null;

                                                    return (
                                                        <div key={group.category}>
                                                            <div className="font-bold text-[10px] text-gray-500 uppercase px-2 py-1 tracking-wide">
                                                                {group.category}
                                                            </div>
                                                            {filteredItems.map((item) => {
                                                                const enrichedItem = {
                                                                    ...item,
                                                                    data_source_id: group.data_source_id,
                                                                };

                                                                return (
                                                                    <div
                                                                        key={item.id}
                                                                        className={`flex justify-between items-center text-xs px-3 py-1.5 rounded hover:bg-gray-100 cursor-pointer ${selectedContexts.some((ctx) => ctx.id === item.id)
                                                                            ? 'bg-gray-100 text-indigo-600'
                                                                            : 'text-gray-700'
                                                                            }`}
                                                                        onClick={() => toggleContext(enrichedItem)}
                                                                    >
                                                                        {item.title}
                                                                        {selectedContexts.some((ctx) => ctx.id === item.id) && (
                                                                            <span className="text-[10px]">✓</span>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    );
                                                })}

                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Row 2: Contexts + Send */}
                            <div className={`flex items-center justify-between flex-wrap gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                                <div className="flex items-center flex-wrap gap-2">
                                    <button
                                        onClick={() => setShowDropdown(!showDropdown)}
                                        title={language === 'ar' ? 'تحديد السياق' : 'Select Context'}
                                        className="w-8 h-8 flex items-center justify-center rounded-full bg-teal-500 hover:bg-teal-600 text-white transition"
                                    >
                                        <Filter className="w-4 h-4" />
                                    </button>

                                    {selectedContexts.map((ctx) => (
                                        <div key={ctx.id} className="px-3 py-1 text-xs bg-gray-100 border border-gray-300 rounded-full text-gray-700 flex items-center gap-1">
                                            #{ctx.title}
                                            <button
                                                onClick={() => toggleContext(ctx)}
                                                className="text-gray-500 hover:text-red-500 text-xs"
                                                title={language === 'ar' ? 'إزالة' : 'Remove'}
                                            >
                                                &times;
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                <button
                                    onClick={handleSend}
                                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition"
                                    title={language === 'ar' ? 'إرسال' : 'Send'}
                                >
                                    <SendHorizonal className={`w-4 h-4 text-gray-500 ${language === 'ar' ? 'transform rotate-180' : ''}`} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>


    );
};

export default ChatAgentPanel;
